/**
 * Copyright Highway9 Networks Inc.
 */
import type { AreaChartSeries, UtilizationData } from "@h9/ui-lib";
import moment from "moment";
import {
  CertPoolSnapshot,
  CertPoolTimeSeriesPoint,
  EdgeMetricsAggregateData,
  IPSEC_CERT_POOL_METRICS,
  IpsecVmcAllocation,
  RawEdgeMetricsAggregateItem,
} from "~/types/edgeMetrics";

export type PoolTimeRange = "24h" | "7d" | "30d";

/** Toggle placeholder chart data (no .env flag). Set to `false` for live API data. */
export const IPSEC_POOL_CHART_USE_DUMMY_DATA = false;

export type VmcAllocationRow = {
  id: string;
  tenant: string;
  vmc: string;
  edges: number;
  manualCert: number;
  /** Auto certs assigned from the IPSEC pool. */
  used: number;
  total: number;
};

const HOUR_SECONDS = 3600;
const DAY_SECONDS = 24 * HOUR_SECONDS;

const TIME_RANGE_CONFIG: Record<
  PoolTimeRange,
  {
    subtract: moment.DurationInputArg1;
    amount: moment.DurationInputArg2;
    resolution: number;
    pointCount: number;
  }
> = {
  "24h": { subtract: 1, amount: "day", resolution: HOUR_SECONDS, pointCount: 24 },
  "7d": { subtract: 7, amount: "day", resolution: DAY_SECONDS, pointCount: 7 },
  "30d": { subtract: 30, amount: "day", resolution: DAY_SECONDS, pointCount: 30 },
};

const DUMMY_POOL_TOTAL = 300;

export function buildIpsecMetricsRequest(timeRange: PoolTimeRange) {
  const endTime = moment().unix();
  const { subtract, amount, resolution } = TIME_RANGE_CONFIG[timeRange];
  const startTime = moment().subtract(subtract, amount).unix();

  return {
    metrics: [...IPSEC_CERT_POOL_METRICS],
    interval: { startTime, endTime },
    resolution,
  };
}

export function isEdgeMetricsAggregateData(value: unknown): value is EdgeMetricsAggregateData {
  if (!value || typeof value !== "object") return false;
  const data = value as EdgeMetricsAggregateData;
  return (
    data.snapshot != null &&
    typeof data.snapshot === "object" &&
    Array.isArray(data.timeSeries)
  );
}

function emptyCertPoolSnapshot(): CertPoolSnapshot {
  return IPSEC_CERT_POOL_METRICS.reduce(
    (snapshot, metric) => ({ ...snapshot, [metric]: 0 }),
    {} as CertPoolSnapshot
  );
}

function parseMetricValue(value: number | string | undefined): number {
  if (value == null) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseMetricTimestamp(value: number | string): string {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return new Date(parsed * 1000).toISOString();
  }
  return new Date(value).toISOString();
}

export function normalizeEdgeMetricsAggregateData(value: unknown): EdgeMetricsAggregateData | null {
  if (isEdgeMetricsAggregateData(value)) {
    return value;
  }
  if (!Array.isArray(value)) {
    return null;
  }

  const snapshot = emptyCertPoolSnapshot();
  const timeSeriesByTimestamp = new Map<string, CertPoolSnapshot>();

  for (const item of value as RawEdgeMetricsAggregateItem[]) {
    if (!IPSEC_CERT_POOL_METRICS.includes(item.metric) || !Array.isArray(item.metricData)) {
      continue;
    }

    for (const [rawTimestamp, rawValue] of item.metricData) {
      const timestamp = parseMetricTimestamp(rawTimestamp);
      const metricValue = parseMetricValue(rawValue);
      const point = timeSeriesByTimestamp.get(timestamp) ?? emptyCertPoolSnapshot();
      point[item.metric] = metricValue;
      timeSeriesByTimestamp.set(timestamp, point);
      snapshot[item.metric] = metricValue;
    }
  }

  const timeSeries = Array.from(timeSeriesByTimestamp.entries())
    .sort(([left], [right]) => new Date(left).getTime() - new Date(right).getTime())
    .map(([timestamp, point]) => ({ ...point, timestamp }));

  return { snapshot, timeSeries };
}

export function snapshotToPoolUtilization(snapshot: CertPoolSnapshot): UtilizationData {
  const used = Math.max(snapshot.cert_pool_total_used, 0);
  const allocated = Math.max(snapshot.cert_pool_total_allocated, used);
  const total = snapshot.cert_pool_total_available;
  const free = Math.max(total - allocated, 0);
  return { used, idle: allocated, free, total };
}

/** Pool chart bars: total pool behind allocated and used absolute counts. */
export function timeSeriesToPoolUtilizationChartSeries(
  timeSeries: CertPoolTimeSeriesPoint[]
): AreaChartSeries[] {
  const totalData: AreaChartSeries["data"] = [];
  const allocatedData: AreaChartSeries["data"] = [];
  const usedData: AreaChartSeries["data"] = [];

  for (const point of timeSeries) {
    const timestamp = new Date(point.timestamp).getTime();
    const used = Math.max(point.cert_pool_total_used, 0);
    const allocated = Math.max(point.cert_pool_total_allocated, used);
    const total = Math.max(point.cert_pool_total_available, 0);

    totalData.push([timestamp, total]);
    allocatedData.push([timestamp, allocated]);
    usedData.push([timestamp, used]);
  }

  return [
    { type: "area", name: "Total", data: totalData },
    { type: "area", name: "Allocated", data: allocatedData },
    { type: "area", name: "Used", data: usedData },
  ];
}

function clampPoolValue(value: number, min: number, max: number): number {
  return Math.round(Math.max(min, Math.min(max, value)));
}

/** Placeholder time series: 24 / 7 / 30 points at the configured resolution. */
export function generateDummyPoolTimeSeries(timeRange: PoolTimeRange): CertPoolTimeSeriesPoint[] {
  const { resolution, pointCount } = TIME_RANGE_CONFIG[timeRange];
  const end = moment();
  const points: CertPoolTimeSeriesPoint[] = [];

  let used = 118;
  let allocated = 152;

  for (let index = 0; index < pointCount; index += 1) {
    const offsetSeconds = (pointCount - 1 - index) * resolution;
    const timestamp = end.clone().subtract(offsetSeconds, "seconds");

    used = clampPoolValue(used + (index % 3 === 0 ? 4 : index % 5 === 0 ? -3 : 1), 85, 175);
    allocated = clampPoolValue(
      allocated + (index % 4 === 1 ? 5 : index % 6 === 2 ? -4 : 0),
      used + 8,
      210
    );

    points.push({
      timestamp: timestamp.toISOString(),
      cert_pool_total_used: used,
      cert_pool_total_allocated: allocated,
      cert_pool_total_available: DUMMY_POOL_TOTAL,
      deployed_edges_total: 42 + (index % 5),
    });
  }

  return points;
}

export function generateDummyPoolChartSeries(timeRange: PoolTimeRange): AreaChartSeries[] {
  return timeSeriesToPoolUtilizationChartSeries(generateDummyPoolTimeSeries(timeRange));
}

export function resolvePoolChartSeries(
  timeRange: PoolTimeRange,
  apiSeries: AreaChartSeries[] | undefined
): AreaChartSeries[] {
  const series = IPSEC_POOL_CHART_USE_DUMMY_DATA
    ? generateDummyPoolChartSeries(timeRange)
    : apiSeries ?? [];
  return cloneAreaChartSeries(series);
}

/** Highcharts mutates series data; clone before passing Redux/Immer-frozen arrays. */
export function cloneAreaChartSeries(series: AreaChartSeries[] | undefined): AreaChartSeries[] {
  if (!Array.isArray(series)) return [];
  return series.map((item) => ({
    ...item,
    data: (item.data ?? []).map(([x, y]) => [x, y]),
  }));
}

export function isIpsecVmcAllocation(value: unknown): value is IpsecVmcAllocation {
  if (!value || typeof value !== "object") return false;
  const row = value as IpsecVmcAllocation;
  return (
    typeof row.id === "string" &&
    typeof row.name === "string" &&
    row.certificateSummary != null &&
    typeof row.certificateSummary === "object"
  );
}

export function isIpsecVmcAllocationArray(value: unknown): value is IpsecVmcAllocation[] {
  return Array.isArray(value) && value.every(isIpsecVmcAllocation);
}

export function filterIpsecVmcAllocationsByActiveDeploymentCloud(
  allocations: IpsecVmcAllocation[],
  deploymentClouds: Array<{ id?: string }>
): IpsecVmcAllocation[] {
  const activeDeploymentCloudIds = new Set(
    deploymentClouds.map((cloud) => cloud.id).filter((id): id is string => Boolean(id))
  );

  return allocations.filter((allocation) => {
    const deploymentCloudId = allocation.deploymentCloud?.id;
    return Boolean(deploymentCloudId && activeDeploymentCloudIds.has(deploymentCloudId));
  });
}

export function mapIpsecVmcAllocationsToRows(allocations: IpsecVmcAllocation[]): VmcAllocationRow[] {
  return allocations.map((item) => {
    const edges = item.edgeSummary?.totalCount ?? item.certificateSummary.deployedEdgeCount ?? 0;
    const used = item.certificateSummary.usedCount ?? 0;
    const total = item.certificateSummary.allocatedCount ?? 0;
    const manualCert = item.certificateSummary.manualAddedCount ?? 0;

    return {
      id: item.id,
      tenant: item.tenantName ?? item.tenantId ?? "",
      vmc: item.name,
      edges,
      manualCert,
      used,
      total,
    };
  });
}
